import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Select,
  Stack,
  useMediaQuery,
  useTheme,
  Collapse,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import CustomModal from '../../../../components/CustomModal';
import CustomDrawer from '../../../../components/CustomDrawer';
import Iconify from '../../../../components/Iconify';
import palette from '../../../../theme/palette';
import Restore from '../../../../assets/withdraw/restore.png';
import useLocales from '../../../../hooks/useLocales';
import Card from '../../../../assets/profile/card.png';
import AffinBank from '../../../../assets/banks/AffinBank.png';
import AllianceBank from '../../../../assets/banks/AllianceBank.png';
import AMBank from '../../../../assets/banks/AMBank.png';
import ArgoBank from '../../../../assets/banks/ArgoBank.png';
import BankIslamMalaysia from '../../../../assets/banks/BankIslamMalaysia.png';
import BankMuamalat from '../../../../assets/banks/BankMuamalat.png';
import BankRakyat from '../../../../assets/banks/BankRakyat.png';
import BSNBank from '../../../../assets/banks/BSNBank.png';
import CIMBBank from '../../../../assets/banks/CIMBBank.png';
import CitiBank from '../../../../assets/banks/CitiBank.png';
import GXBank from '../../../../assets/banks/GXBank.png';
import HongLeongBank from '../../../../assets/banks/HongLeongBank.png';
import HSBCBank from '../../../../assets/banks/HSBCBank.png';
import Maybank from '../../../../assets/banks/Maybank.png';
import MerchanttradeBank from '../../../../assets/banks/MerchantradeBank.png';
import OCBCBank from '../../../../assets/banks/OCBCBank.png';
import PublicBank from '../../../../assets/banks/PublicBank.png';
import RHBBank from '../../../../assets/banks/RHBBank.png';
import SecurePay from '../../../../assets/banks/SecurePay.png';
import StandardCharteredBank from '../../../../assets/banks/StandardCharteredBank.png';
import TouchGo from '../../../../assets/banks/TouchGo.png';
import UOBBank from '../../../../assets/banks/UOBBank.png';
import { conObjForm } from '../../../../utils/connection-assistant';

const banksIcons = {
  maybank: Maybank,
  cimbbank: CIMBBank,
  publicbank: PublicBank,
  hongleongbank: HongLeongBank,
  rhbbank: RHBBank,
  ambank: AMBank,
  affinbank: AffinBank,
  bsnbank: BSNBank,
  alliancebank: AllianceBank,
  bankislammalaysia: BankIslamMalaysia,
  hsbcbank: HSBCBank,
  uobbank: UOBBank,
  citibank: CitiBank,
  ocbcbank: OCBCBank,
  standardcharteredbank: StandardCharteredBank,
  argobank: ArgoBank,
  bankmuamalat: BankMuamalat,
  bankrakyat: BankRakyat,
  gxbank: GXBank,
  merchanttradebank: MerchanttradeBank,
  securepay: SecurePay,
  touchandgo: TouchGo,
};

const getBankKey = (name) => {
  return name
    ?.toLowerCase()
    .replace(/&/g, 'and')
    .replace(/\s+/g, '')
    .replace(/[^a-z]/g, '');
};
const BankOption = ({ item, isSelected, onSelect }) => {
  const bankIcon = banksIcons[getBankKey(item.bank_name)] || Card; // Get the bank icon
  const theme = useTheme();
  return (
    <Box
      onClick={() => onSelect(item.bank_id)}
      sx={{
        p: 2,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        border: `2px solid ${isSelected ? theme.palette.primary.main : '#F3F4F6'}`,
        background: isSelected ? theme.palette.gradients.buttonPrimary : '#F9FAFB',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Bank icon and name */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <img src={bankIcon} alt={item.bank_name} width={28} height={28} />
        <Typography fontWeight={700} sx={{ color: '#000' }}>
          {item.bank_name}
        </Typography>
      </Box>

      {/* Checkbox if selected */}
      {isSelected && (
        <Iconify icon="mdi:checkbox-marked" sx={{ color: theme.palette.primary.main, fontSize: '24px' }} />
      )}
    </Box>
  );
};

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: '#eaebec',
    height: 48,
    '& fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: '1px solid #E5E7EB',
    },
  },
  '& input': {
    padding: '12px',
    fontWeight: 400,
    '&::placeholder': {
      color: '#8991A7',
      opacity: 1,
    },
    '&:-ms-input-placeholder': {
      color: '#8991A7',
    },
    '&::-ms-input-placeholder': {
      color: '#8991A7',
    },
  },
};
const AddBankAccountDialog = ({ handleClose, handleOpen, onSuccess }) => {
  const { translate } = useLocales();
  const [selectedBankId, setSelectedBankId] = useState('');
  const [isBankListOpen, setIsBankListOpen] = useState(false);
  const [holder, setHolder] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [isBankListExpanded, setIsBankListExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const { bank } = useSelector((state) => state.lookup);
  const isValid = selectedBankId !== '' && holder.trim() !== '' && accountNumber.trim() !== '';
  const selectedBankObj = bank?.find((b) => b.bank_id === selectedBankId);
  const selectedBankIcon = banksIcons[getBankKey(selectedBankObj?.bank_name)] || Card;
  const selectedBankName =
    bank?.find((item) => item.bank_id === selectedBankId)?.bank_name || translate('select_your_bank');

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');

      const payload = {
        'add_user_withdraw_bank[bank_id]': selectedBankId,
        'add_user_withdraw_bank[account_holder_name]': holder,
        'add_user_withdraw_bank[account_number]': accountNumber,
        'add_user_withdraw_bank[default]': 2,
      };

      const { data: response } = await conObjForm.post('v1/user/add-withdraw-bank', payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response?.success) {
        enqueueSnackbar('Bank account added successfully!', {
          variant: 'success',
        });

        setSelectedBankId('');
        setHolder('');
        setAccountNumber('');
        setIsBankListExpanded(false);
        handleClose();

        if (onSuccess) {
          onSuccess();
        }
      } else {
        enqueueSnackbar(response?.message || translate('add_bank_failed'), { variant: 'error' });
      }
    } catch (error) {
      const data = error.response?.data;

      if (data?.error && Array.isArray(data.error)) {
        data.error.forEach((err) => {
          enqueueSnackbar(err, { variant: 'error' });
        });
      } else if (data?.message) {
        enqueueSnackbar(data.message, { variant: 'error' });
      } else {
        enqueueSnackbar('Submission failed', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const renderForm = (
    <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 2.5, height: '400px' }}>
      {!isMobile && (
        <Typography variant="h6" fontWeight={700} textAlign="center" mb={1}>
          {translate('add_bank_account')}
        </Typography>
      )}

      <Box>
        <Typography variant="body2" fontWeight={600} mb={1}>
          {translate('preferred_bank')}
        </Typography>

        {isMobile ? (
          /* MOBILE VIEW: Trigger Button for Drawer (Keep your existing code) */
          <Button
            fullWidth
            onClick={() => setIsBankListOpen(true)}
            sx={{
              justifyContent: 'space-between',
              p: 2,
              borderRadius: '12px',
              bgcolor: selectedBankId ? theme.palette.gradients.buttonPrimary : '#F3F4F6',
              border: selectedBankId ? `2px solid ${theme.palette.primary.main}` : '1px solid #E5E7EB',
              textTransform: 'none',
              color: '#000',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {selectedBankId ? (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  <img src={selectedBankIcon} alt={selectedBankObj?.bank_name} width={20} height={20} />
                </Box>
              ) : (
                <Iconify icon="mdi:bank" sx={{ color: '#6B7280' }} />
              )}
              <Typography fontWeight={700}>{selectedBankName}</Typography>
            </Stack>
            <Iconify icon="mdi:chevron-right" />
          </Button>
        ) : (
          <Select
            fullWidth
            value={selectedBankId}
            onChange={(e) => setSelectedBankId(e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src={Card} alt="bank card" width={40} height={40} />
                    {translate('select_bank')}
                  </Box>
                );
              }

              const selectedBank = bank.find((b) => b.bank_id === selected);
              const icon = banksIcons[getBankKey(selectedBank?.bank_name)] || Card;

              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <img src={icon} width={40} alt="icon" height={40} />
                  {selectedBank?.bank_name}
                </Box>
              );
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 280,
                  overflowY: 'auto',
                  borderRadius: '10px',
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#F3F4F6',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#A0A0A0',
                  },
                },
              },
            }}
            sx={{
              borderRadius: '8px',
              bgcolor: '#F3F4F6',
              ...(selectedBankId !== '' && {
                // border: `2px solid ${theme.palette.primary.main}`,
                background: `${theme.palette.gradients.buttonPrimary} !important`,
              }),
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
              },
              '& .MuiSelect-icon': { color: '#000' },
            }}
          >
            {/* Default */}
            <MenuItem value="">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <img src={Card} width={40} alt="card" height={40} />
                {translate('select_bank')}
              </Box>
            </MenuItem>

            {/* Banks */}
            {bank?.map((item) => {
              const isSelected = item.bank_id === selectedBankId;
              const icon = banksIcons[getBankKey(item.bank_name)] || Card;

              return (
                <MenuItem
                  key={item.bank_id}
                  value={item.bank_id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontWeight: isSelected ? 700 : 500,
                    '&.Mui-selected': {
                      background: theme.palette.gradients.buttonPrimary,
                      color: '#000',
                      '&:hover': {
                        background: theme.palette.gradients.buttonPrimary2,
                      },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src={icon} alt="bank icons" width={40} height={40} />
                    {item.bank_name}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        )}
      </Box>

      <Box>
        <TextField
          placeholder={translate('account_holder_name')}
          fullWidth
          value={holder}
          onChange={(e) => setHolder(e.target.value)}
          sx={inputStyles}
        />
      </Box>
      <Box>
        <TextField
          placeholder={translate('account_number')}
          fullWidth
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
          sx={inputStyles}
        />
      </Box>

      {isMobile && (
        <CustomDrawer open={isBankListOpen} onClose={() => setIsBankListOpen(false)} title={translate('select_bank')}>
          <Box sx={{ p: 2, pb: 6, height: '500px' }}>
            <Stack spacing={1.5}>
              {bank?.map((item) => (
                <BankOption
                  key={item.bank_id}
                  item={item}
                  isSelected={selectedBankId === item.bank_id}
                  onSelect={(id) => {
                    setSelectedBankId(id);
                    setIsBankListOpen(false);
                  }}
                />
              ))}
            </Stack>
          </Box>
        </CustomDrawer>
      )}
    </Box>
  );

  const renderActions = (
    <Box sx={{ p: isMobile ? 2 : 0, width: '100%', pb: isMobile ? 4 : 0 }}>
      <Stack
        // Row for mobile (side-by-side), Column-Reverse for desktop (Submit on top)
        direction={isMobile ? 'row' : 'column-reverse'}
        spacing={2}
      >
        {/* Cancel Button */}
        <Button
          fullWidth
          onClick={handleClose}
          variant="outlined"
          color="primary"
          sx={{
            textTransform: 'none',
            borderRadius: '30px',
            fontWeight: 500,
            py: 1.5,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#E5E7EB !important',
            },
          }}
        >
          {translate('cancel')}
        </Button>

        {/* Submit Button */}
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || loading}
          fullWidth
          sx={{
            textTransform: 'none',
            borderRadius: '30px',
            fontWeight: 700,
            py: 1.5,
            color: '#000 !important',
            background: theme.palette.gradients.buttonPrimary,
            boxShadow: 'none',
            '&:hover': {
              background: theme.palette.gradients.buttonPrimary2,
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              background: '#F3F4F6',
              color: '#9CA3AF',
              border: 'none',
            },
          }}
        >
          {loading ? translate('processing') : translate('submit')}
        </Button>
      </Stack>
    </Box>
  );

  return isMobile ? (
    <CustomDrawer open={handleOpen} onClose={handleClose} title={translate('add_bank_account')} actions={renderActions}>
      {renderForm}
    </CustomDrawer>
  ) : (
    <CustomModal hasClose maxWidth="xs" open={handleOpen} onClose={handleClose} actions={renderActions}>
      {renderForm}
    </CustomModal>
  );
};

export default AddBankAccountDialog;
